import React, { useState, useEffect } from "react";
import { Row, Col } from "reactstrap";
import {
  Button as AntdButton,
  Input,
  InputNumber,
  Table,
  Switch,
  Tooltip,
  message,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";

export interface Stage {
  workOrderLineStageId?: number; // Only for existing stages during update
  stageTitle: string;
  splitPercentage: number;
  amount?: number; // Calculated field for display
}

interface StageBreakdownProps {
  rate: number;
  stages: Stage[];
  onChange: (stages: Stage[]) => void;
  disabled?: boolean;
  showToggle?: boolean;
  isEnabled?: boolean;
  onToggleChange?: (enabled: boolean) => void;
  deletedStageIds?: number[];
  onDeletedStageIdsChange?: (ids: number[]) => void;
}

const StageBreakdown: React.FC<StageBreakdownProps> = ({
  rate,
  stages,
  onChange,
  disabled = false,
  showToggle = true,
  isEnabled = false,
  onToggleChange,
  deletedStageIds = [],
  onDeletedStageIdsChange,
}) => {
  const [enabled, setEnabled] = useState(isEnabled);

  useEffect(() => {
    setEnabled(isEnabled);
  }, [isEnabled]);

  const handleToggleChange = (checked: boolean) => {
    setEnabled(checked);
    if (onToggleChange) {
      onToggleChange(checked);
    }
    if (!checked) {
      // Clear stages when disabled
      onChange([]);
    }
  };

  const addStage = () => {
    const totalPercentage = stages.reduce(
      (sum, stage) => sum + (stage.splitPercentage || 0),
      0
    );
    const remainingPercentage = Math.max(0, 100 - totalPercentage);

    const newStage: Stage = {
      stageTitle: `Stage ${stages.length + 1}`,
      splitPercentage: remainingPercentage,
      amount: (rate * remainingPercentage) / 100,
    };
    onChange([...stages, newStage]);
  };

  const removeStage = (index: number) => {
    const stageToRemove = stages[index];

    // If the stage has an ID (existing stage), add to deleted IDs
    if (stageToRemove.workOrderLineStageId && onDeletedStageIdsChange) {
      onDeletedStageIdsChange([
        ...deletedStageIds,
        stageToRemove.workOrderLineStageId,
      ]);
    }

    const newStages = stages.filter((_, i) => i !== index);
    onChange(newStages);
  };

  const updateStage = (
    index: number,
    field: keyof Stage,
    value: string | number
  ) => {
    const newStages = [...stages];
    newStages[index] = {
      ...newStages[index],
      [field]: value,
    };

    // Recalculate amount when percentage changes
    if (field === "splitPercentage") {
      newStages[index].amount = (rate * (value as number)) / 100;
    }

    onChange(newStages);
  };

  const totalPercentage = stages.reduce(
    (sum, stage) => sum + (stage.splitPercentage || 0),
    0
  );

  const totalAmount = stages.reduce(
    (sum, stage) => sum + ((rate * (stage.splitPercentage || 0)) / 100),
    0
  );

  const isValid = totalPercentage <= 100;

  const columns = [
    {
      title: "Stage Title",
      dataIndex: "stageTitle",
      key: "stageTitle",
      width: "40%",
      render: (text: string, record: Stage, index: number) => (
        <Input
          value={text}
          onChange={(e) => updateStage(index, "stageTitle", e.target.value)}
          placeholder="Enter stage title"
          disabled={disabled}
          size="small"
        />
      ),
    },
    {
      title: "Amount (%)",
      dataIndex: "splitPercentage",
      key: "splitPercentage",
      width: "25%",
      render: (value: number, record: Stage, index: number) => (
        <InputNumber
          value={value}
          onChange={(val) => updateStage(index, "splitPercentage", val || 0)}
          min={0}
          max={100}
          formatter={(value) => `${value}%`}
          parser={(value) => value!.replace("%", "") as unknown as number}
          disabled={disabled}
          size="small"
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: "25%",
      render: (_: any, record: Stage) => {
        const amount = (rate * (record.splitPercentage || 0)) / 100;
        return (
          <span style={{ fontWeight: 500 }}>
            ₹{amount.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
          </span>
        );
      },
    },
    {
      title: "",
      key: "action",
      width: "10%",
      render: (_: any, record: Stage, index: number) => (
        <AntdButton
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => removeStage(index)}
          disabled={disabled}
          size="small"
        />
      ),
    },
  ];

  return (
    <div className="stage-breakdown-container">
      {showToggle && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: enabled ? 16 : 0,
            gap: 8,
          }}
        >
          <Switch
            checked={enabled}
            onChange={handleToggleChange}
            disabled={disabled}
            size="small"
          />
          <span style={{ fontWeight: 500 }}>Enable Schedule Breakdown</span>
          <Tooltip title="Split the rate into multiple payment stages based on percentage">
            <InfoCircleOutlined style={{ color: "#1890ff", cursor: "help" }} />
          </Tooltip>
        </div>
      )}

      {(enabled || !showToggle) && (
        <div
          style={{
            border: "1px solid #d9d9d9",
            borderRadius: 4,
            padding: 16,
            backgroundColor: "#fafafa",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 12,
            }}
          >
            <span style={{ fontWeight: 500, fontSize: 13 }}>
              Rate: ₹{rate.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
            </span>
            <AntdButton
              type="dashed"
              icon={<PlusOutlined />}
              onClick={addStage}
              disabled={disabled || totalPercentage >= 100}
              size="small"
            >
              Add Stage
            </AntdButton>
          </div>

          {stages.length > 0 ? (
            <>
              <Table
                dataSource={stages.map((stage, index) => ({
                  ...stage,
                  key: index,
                }))}
                columns={columns}
                pagination={false}
                size="small"
                bordered
              />
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginTop: 12,
                  padding: "8px 12px",
                  backgroundColor: isValid ? "#f6ffed" : "#fff2f0",
                  border: `1px solid ${isValid ? "#b7eb8f" : "#ffa39e"}`,
                  borderRadius: 4,
                }}
              >
                <span
                  style={{
                    fontWeight: 600,
                    color: isValid ? "#52c41a" : "#ff4d4f",
                  }}
                >
                  Total: ₹
                  {totalAmount.toLocaleString("en-IN", {
                    maximumFractionDigits: 2,
                  })}{" "}
                  ({totalPercentage}%)
                  {!isValid && " - Exceeds 100%!"}
                  {isValid && totalPercentage === 100 && " ✓"}
                </span>
              </div>
            </>
          ) : (
            <div
              style={{
                textAlign: "center",
                padding: 24,
                color: "#999",
              }}
            >
              No stages added. Click "Add Stage" to create payment milestones.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StageBreakdown;
